import { useRef, useSyncExternalStore } from 'react'

import { isSubTimerRunning } from '~shared/rundown/sub-timer-run'
import type { SubTimerState } from '~shared/rundown/sub-timer-run'

const TICK_MS = 250

let tick = 0
let interval: ReturnType<typeof setInterval> | null = null
const listeners = new Set<() => void>()

/** One shared interval, running only while a live timer is on screen. */
function subscribeTick(listener: () => void) {
  listeners.add(listener)
  interval ??= setInterval(() => {
    tick++
    listeners.forEach((l) => l())
  }, TICK_MS)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && interval) {
      clearInterval(interval)
      interval = null
    }
  }
}

const subscribeNever = () => () => {}

/**
 * Server time, re-rendering every tick only while `isLive(now)` holds. Idle,
 * paused and finished timers read the time once and stay put.
 *
 * @param timeOffset Server minus local clock, in ms.
 */
export function useSubTimerClock(
  timeOffset: number,
  isLive: (now: number) => boolean,
): number {
  // Reading the clock each render is the point: the tick re-renders.
  // oxlint-disable-next-line react/purity
  const now = Date.now() + timeOffset
  const live = isLive(now)
  useSyncExternalStore(live ? subscribeTick : subscribeNever, () =>
    live ? tick : -1,
  )
  return now
}

/** Server time for one sub timer, ticking only while it runs. */
export function useSubTimerNow(
  timer: SubTimerState,
  timeOffset: number,
): number {
  return useSubTimerClock(timeOffset, (now) => isSubTimerRunning(timer, now))
}

/**
 * Scrolls a truncated element back and forth so its full text can be read.
 * Wire `start`/`stop` to hover on whatever area should trigger it.
 */
export function useTicker<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const animation = useRef<Animation | null>(null)

  const stop = () => {
    animation.current?.cancel()
    animation.current = null
    ref.current?.style.removeProperty('overflow')
    ref.current?.style.removeProperty('text-overflow')
  }

  const start = () => {
    const el = ref.current
    if (!el) return
    const distance = el.scrollWidth - el.clientWidth
    if (distance <= 0) return
    stop()
    el.style.overflow = 'visible'
    el.style.textOverflow = 'clip'
    const from = 'translateX(0)'
    const to = `translateX(${-distance}px)`
    animation.current = el.animate(
      [
        { transform: from, offset: 0 },
        { transform: from, offset: 0.2 },
        { transform: to, offset: 0.8 },
        { transform: to, offset: 1 },
      ],
      {
        duration: 1500 + distance * 40,
        iterations: Infinity,
        direction: 'alternate',
        easing: 'ease-in-out',
      },
    )
  }

  return { ref, start, stop }
}
